import type { Database } from '@/database.types';
import { goalSchema } from '@/entities/goal/schema';
import type { Goal, GoalCreate, GoalUpdate } from '@/entities/goal/types';

type ApiGoalUpdate = Omit<
  Database['public']['Tables']['goals']['Update'],
  'user_id'
> & {
  user_id?: string;
};

type ApiGoalInsert = Omit<
  Database['public']['Tables']['goals']['Insert'],
  'user_id'
> & {
  user_id?: string;
};

function toApiGoalCreate(goalCreate: GoalCreate): ApiGoalInsert {
  return {
    id: goalCreate.id,
    title: goalCreate.title,
    status: goalCreate.status,
    done_at: goalCreate.doneAt?.toISOString() || null,
    done_at_local: goalCreate.doneAtLocal || null,
    parent_goal_id: goalCreate.parentGoalId ?? null,
  };
}

function toApiGoalUpdate(goalUpdate: GoalUpdate): ApiGoalUpdate {
  return {
    title: goalUpdate.title,
    status: goalUpdate.status,
    done_at: goalUpdate.doneAt
      ? goalUpdate.doneAt.toISOString()
      : goalUpdate.doneAt,
    done_at_local: goalUpdate.doneAtLocal,
  };
}

function toGoal(data: Database['public']['Tables']['goals']['Row']): Goal {
  return goalSchema.parse({
    id: data.id,
    createdAt: new Date(data.created_at),
    updatedAt: new Date(data.updated_at),
    title: data.title,
    status: data.status,
    doneAtLocal: data.done_at_local,
    doneAt: data.done_at ? new Date(data.done_at) : null,
    parentGoalId: data.parent_goal_id ?? null,
  });
}

export function createGoalBatcher(
  fetchByIds: (ids: string[]) => Promise<Goal[]>,
) {
  let queue: {
    id: string;
    resolve: (g: Goal | null) => void;
    reject: (e: unknown) => void;
  }[] = [];
  let scheduled = false;

  async function flush() {
    const batch = queue;
    queue = [];
    scheduled = false;

    const uniqueIds = [...new Set(batch.map((e) => e.id))];
    try {
      const goals = await fetchByIds(uniqueIds);
      const goalMap = new Map(goals.map((g) => [g.id, g]));
      for (const entry of batch) {
        entry.resolve(goalMap.get(entry.id) ?? null);
      }
    } catch (error) {
      for (const entry of batch) {
        entry.reject(error);
      }
    }
  }

  return {
    load(id: string): Promise<Goal | null> {
      return new Promise((resolve, reject) => {
        queue.push({ id, resolve, reject });
        if (!scheduled) {
          scheduled = true;
          queueMicrotask(() => void flush());
        }
      });
    },
  };
}

export function createGoalsService(
  supabaseClient: typeof import('./supabase').supabaseClient,
  getUserId: () => string,
) {
  const readGoalsByIds = async (goalIds: string[]): Promise<Goal[]> => {
    if (goalIds.length === 0) {
      return [];
    }
    const userId = getUserId();
    const { data, error } = await supabaseClient
      .from('goals')
      .select('*')
      .in('id', goalIds)
      .eq('user_id', userId);
    if (error) {
      throw error;
    }
    return data.map(toGoal);
  };

  const batcher = createGoalBatcher(readGoalsByIds);

  return {
    createGoals: async (goals: GoalCreate[]): Promise<Goal[]> => {
      const userId = getUserId();
      const payload = goals.map((g) => ({
        ...toApiGoalCreate(g),
        user_id: userId,
      }));

      const { data, error } = await supabaseClient
        .from('goals')
        .insert(payload)
        .select('*');

      if (error) {
        throw error;
      }

      return data.map(toGoal);
    },

    readGoal: (goalId: string): Promise<Goal | null> => {
      return batcher.load(goalId);
    },

    updateGoal: async (
      goalId: string,
      goalUpdate: GoalUpdate,
    ): Promise<Goal> => {
      const userId = getUserId();
      const apiGoalUpdate = toApiGoalUpdate(goalUpdate);

      const { data, error } = await supabaseClient
        .from('goals')
        .update({ ...apiGoalUpdate, user_id: userId })
        .eq('id', goalId)
        .select('*')
        .single();

      if (error) {
        throw error;
      }

      return toGoal(data);
    },

    deleteGoal: async (goalId: string): Promise<void> => {
      const { error } = await supabaseClient
        .from('goals')
        .delete()
        .eq('id', goalId);

      if (error) {
        throw error;
      }
    },
  };
}
